'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import usePWAInstall from '../../hooks';
import { Step } from './components/Step';
import {
  getStepsAndroid,
  getStepsAndroidPWA,
  getStepsPWA1,
  getStepsPWA2,
  getStepsPWA3,
} from './helpers';
import { isIos as checkIsIos } from '@/helpers/common';
import { useCommonStore } from '@/store';

import {
  ScDescription,
  ScMobileAppPageContainer,
  ScSteps,
  ScStepsContainer,
  ScStepsTitle,
  ScTitle,
} from './styled';
import { ScContainer } from '@/styles/common';

export const MobileAppPage = () => {
  const { t } = useTranslation('mobileApp');

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const mobileAppCMS = useCommonStore.use.mobileAppCMS();
  const pwaInstall = usePWAInstall();

  const stepsAndroid = getStepsAndroid(isMobileDevice, t, mobileAppCMS);
  const stepsAndroidPWA = getStepsAndroidPWA(isMobileDevice, t, pwaInstall);
  const isIos = checkIsIos();

  const steps1PWA = getStepsPWA1(isMobileDevice, t);

  const steps2PWA = getStepsPWA2(t);

  const steps3PWA = getStepsPWA3(t);

  const iosPWA = useMemo(
    () => (
      <ScStepsContainer>
        <ScStepsTitle>
          {isMobileDevice ? t('new.pwa.title-1-mob') : t('new.pwa.title-1')}
        </ScStepsTitle>
        <ScSteps cols={steps1PWA.length}>
          {steps1PWA.map((item, index) => (
            <Step {...item} num={index} key={index} />
          ))}
        </ScSteps>
      </ScStepsContainer>
    ),
    [isMobileDevice, steps1PWA, t]
  );

  const androidPWA = useMemo(
    () => (
      <ScStepsContainer>
        <ScStepsTitle>{t('new.androidPWA.title')}</ScStepsTitle>
        <ScSteps cols={stepsAndroidPWA.length}>
          {stepsAndroidPWA.map((item, index) => (
            <Step {...item} num={index} key={index} />
          ))}
        </ScSteps>
      </ScStepsContainer>
    ),
    [stepsAndroidPWA, t]
  );

  const androidApp = useMemo(
    () => (
      <ScStepsContainer>
        <ScStepsTitle>{t('new.android.title')}</ScStepsTitle>
        <ScSteps cols={stepsAndroid.length}>
          {stepsAndroid.map((item, index) => (
            <Step {...item} num={index} key={index} />
          ))}
        </ScSteps>
      </ScStepsContainer>
    ),
    [stepsAndroid, t]
  );

  const mobileIntructions = useMemo(() => {
    if (isMobileDevice) {
      if (isIos) {
        return iosPWA;
      }

      if (mobileAppCMS?.showAndroid) {
        return androidApp;
      }

      return androidPWA;
    }

    if (mobileAppCMS?.showAndroid) {
      return (
        <>
          {androidApp}
          {iosPWA}
        </>
      );
    }

    return (
      <>
        {androidPWA}
        {iosPWA}
      </>
    );
  }, [
    androidApp,
    androidPWA,
    iosPWA,
    isIos,
    isMobileDevice,
    mobileAppCMS?.showAndroid,
  ]);

  return (
    <ScMobileAppPageContainer>
      <ScContainer>
        <ScTitle>{t('new.title')}</ScTitle>
        <ScDescription>{t('new.description-main')}</ScDescription>
        {mobileIntructions}
        {!isMobileDevice && (
          <ScStepsContainer>
            <ScStepsTitle>{t('new.pwa.title-2')}</ScStepsTitle>
            <ScSteps cols={steps2PWA.length}>
              {steps2PWA.map((item, index) => (
                <Step {...item} num={index} key={index} />
              ))}
            </ScSteps>
          </ScStepsContainer>
        )}
        {!isMobileDevice && (
          <ScStepsContainer>
            <ScStepsTitle>{t('new.pwa.title-3')}</ScStepsTitle>
            <ScSteps cols={steps3PWA.length}>
              {steps3PWA.map((item, index) => (
                <Step {...item} num={index} key={index} />
              ))}
            </ScSteps>
          </ScStepsContainer>
        )}
      </ScContainer>
    </ScMobileAppPageContainer>
  );
};
